import { linesOf } from './textParcel';

/*
 * A whole board, doc or folder sent to Artwork (Open with on a whole thing, owner 2 Nov 2026: "Yes, add it"). It lands the way
 * a layered picture does, read as layered files: each group an artboard of its own named after it, with what isn't in a group
 * on the first, its pictures in a grid at their own proportions and its words as live text above them. A long group runs on
 * to "Name (2)": a page stays small enough to work on, and the canvas draws only the pages in view. Clips, sounds and other
 * files stay behind, and the page says so. Nothing is uploaded again: a picture layer points at the stored file.
 */

/** The most pictures on one artboard. Above it a group runs on to another: past eight across a picture is too small to pick. */
export const PICTURES_PER_BOARD = 64;
const PAGE = { w: 1920, margin: 80, gap: 24, textGap: 40 } as const;
const BODY = { size: 40, lineHeight: 1.35, weight: 500 } as const;
const HEAD = { size: 64, lineHeight: 1.15, weight: 700 } as const;

type Raw = Record<string, unknown>;
interface Picture { mediaAssetId: string; url: string; mime: string; name: string; width: number; height: number }
interface Words { name: string | null; text: string }
interface Section { name: string | null; pictures: Picture[]; words: Words[] }

const positive = (v: unknown) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : 0);
const isPicture = (item: Raw) => /^image\//i.test(String(item.mime || '')) && Boolean(item.mediaAssetId) && /^https:\/\//i.test(String(item.url || ''));
const counted = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** What a parcel leaves behind, by why: "2 clips, 1 sound and 3 other files". "" when it all comes. */
export function behindWords(items: Raw[]): string {
  const left = items.filter((item) => !isPicture(item));
  const of = (test: RegExp) => left.filter((item) => test.test(String(item.mime || ''))).length;
  const clips = of(/^video\//i), sounds = of(/^audio\//i), other = left.length - clips - sounds;
  const parts = [clips ? counted(clips, 'clip') : '', sounds ? counted(sounds, 'sound') : '', other ? counted(other, 'other file') : ''].filter(Boolean);
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : (parts[0] ?? '');
}

/** The parcel as sections: what's in no group first, then each group that holds something, in the order they were sent. */
function sectionsOf(parcel: Raw): Section[] {
  const whole = ((parcel.payload as Raw | null)?.whole ?? {}) as Raw;
  const groups = Array.isArray(whole.groups) ? (whole.groups as Raw[]) : [];
  const groupOf = Array.isArray(whole.groupOf) ? (whole.groupOf as unknown[]) : [];
  const slot = (v: unknown) => (Number.isInteger(v) && groups[v as number] ? (v as number) : -1);
  const held = new Map<number, Section>();
  const section = (at: number) => {
    if (!held.has(at)) held.set(at, { name: at < 0 ? null : String(groups[at]?.name || 'Untitled').slice(0, 200), pictures: [], words: [] });
    return held.get(at)!;
  };
  (Array.isArray(parcel.items) ? (parcel.items as Raw[]) : []).forEach((item, i) => {
    if (!isPicture(item)) return;
    section(slot(groupOf[i])).pictures.push({ mediaAssetId: String(item.mediaAssetId), url: String(item.url), mime: String(item.mime), name: String(item.name || `Picture ${i + 1}`).slice(0, 200), width: positive(item.width), height: positive(item.height) });
  });
  for (const t of Array.isArray(whole.texts) ? (whole.texts as Raw[]) : []) {
    if (typeof t?.text === 'string' && t.text.trim()) section(slot(t.group)).words.push({ name: typeof t.name === 'string' ? t.name.trim() || null : null, text: t.text.trim() });
  }
  return [...held.keys()].sort((a, b) => a - b).map((at) => held.get(at)!);
}

/** Columns for `n` pictures: as near a square as eight across allows. */
const columnsFor = (n: number) => Math.min(8, Math.max(1, Math.ceil(Math.sqrt(n))));

/** One artboard as a `flobi.layers` file: its words top to bottom, then its pictures in columns. */
function boardFile(name: string, words: Words[], pictures: Picture[]): Raw {
  const width = PAGE.w - PAGE.margin * 2;
  const layers: Raw[] = [];
  let y = PAGE.margin;
  const addText = (text: string, font: typeof BODY | typeof HEAD, label: string) => {
    const h = Math.ceil(linesOf(text, width, font.size) * font.size * font.lineHeight);
    layers.push({
      id: `text-${layers.length + 1}`, kind: 'text', name: label.slice(0, 200), bbox: { x: PAGE.margin, y, w: width, h }, z: layers.length + 1, wrap: true, text,
      style: { fontFamily: 'Inter', fontWeight: font.weight, fontSizePx: font.size, color: '#111111', letterSpacingPx: 0, lineHeight: font.lineHeight, align: 'left', alignY: 'top' },
    });
    y += h;
  };
  for (const piece of words) {
    if (piece.name && !piece.text.startsWith(piece.name)) {
      addText(piece.name, HEAD, piece.name);
      y += Math.round(HEAD.size * 0.4);
    }
    addText(piece.text, BODY, piece.name || piece.text.split(/\n/)[0]!.slice(0, 60) || 'Text');
    y += PAGE.textGap;
  }
  const columns = columnsFor(pictures.length);
  const cell = (width - PAGE.gap * (columns - 1)) / columns;
  // Each picture goes under the shortest column, so pictures of mixed shapes leave no holes.
  const tops: number[] = Array.from({ length: columns }, () => y);
  pictures.forEach((picture, index) => {
    // A picture with no size is shown whole in a square; one with a size takes the cell's width at its own proportions.
    const known = picture.width > 0 && picture.height > 0;
    const h = known ? Math.round(cell * (picture.height / picture.width)) : Math.round(cell);
    const column = tops.indexOf(Math.min(...tops));
    layers.push({ id: `picture-${index + 1}`, kind: 'image', name: picture.name, result: index, z: layers.length + 1, bbox: { x: PAGE.margin + column * (cell + PAGE.gap), y: tops[column]!, w: cell, h }, ...(known ? {} : { fit: { mode: 'contain' } }) });
    tops[column]! += h + PAGE.gap;
  });
  const bottom = pictures.length ? Math.max(...tops) - PAGE.gap : y - PAGE.textGap;
  const height = Math.max(PAGE.w / 2, Math.ceil(bottom + PAGE.margin));
  return { kind: 'flobi.layers', version: 1, name, board: name, model: 'layers', editableText: words.length > 0, canvas: { w: PAGE.w, h: height }, size: { width: PAGE.w, height }, fonts: [], layers, pictures, preview: null };
}

/**
 * A whole parcel (its raw JSON) as the layered files it lands as, one an artboard, or null when it holds nothing Artwork takes.
 * `behind` names what stays behind, `texts` counts the pieces of words sent (one with a name is two layers), `preview` is
 * what the page shows of it.
 */
export function wholeLayerFiles(parcel: Raw): { files: Raw[]; behind: string; texts: number; preview: string | null } | null {
  const sent = String(parcel.name || '').trim() || 'Sent';
  const files: Raw[] = [];
  const sections = sectionsOf(parcel);
  for (const section of sections) {
    const name = section.name ?? sent;
    const parts = Math.max(1, Math.ceil(section.pictures.length / PICTURES_PER_BOARD));
    for (let part = 0; part < parts; part += 1) {
      files.push(boardFile(part ? `${name} (${part + 1})` : name, part ? [] : section.words, section.pictures.slice(part * PICTURES_PER_BOARD, (part + 1) * PICTURES_PER_BOARD)));
    }
  }
  if (!files.length) return null;
  const items = Array.isArray(parcel.items) ? (parcel.items as Raw[]) : [];
  const shown = typeof parcel.preview === 'string' && /^https:\/\//i.test(parcel.preview) ? parcel.preview : null;
  return { files, behind: behindWords(items), texts: sections.reduce((n, s) => n + s.words.length, 0), preview: shown ?? (items.find(isPicture)?.url as string | undefined) ?? null };
}
